import { useState } from "react";
import axios from "axios";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { Building2, Send, Search, ShieldCheck, MapPin, Clock } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { EMPLOYER_DASH } from "@/lib/data";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const TONE = {
  green: "text-[#10B981]", amber: "text-[#FFB800]", blue: "text-[#0099FF]", ember: "text-[#FF5E1E]",
};

export default function Employers() {
  const [form, setForm] = useState({ company: "", contact_name: "", contact_email: "", trade: "Structural Welders", num_workers: 25, location: "", start_date: "", shift: "", notes: "" });
  const [busy, setBusy] = useState(false);
  const [matches, setMatches] = useState(null);
  const [matching, setMatching] = useState(false);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const findMatches = async (trade) => {
    setMatching(true);
    try {
      const { data } = await axios.get(`${API}/employer/matches`, { params: trade ? { trade } : {} });
      setMatches(data);
      if (data.length === 0) toast.info("No workforce-ready matches for that trade yet.");
    } catch {
      toast.error("Could not load matches.");
    } finally {
      setMatching(false);
    }
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.company || !form.contact_email || !form.trade) {
      toast.error("Company, email and trade are required.");
      return;
    }
    setBusy(true);
    try {
      await axios.post(`${API}/employer-requests`, { ...form, num_workers: Number(form.num_workers) || 1 });
      toast.success("Talent request received", { description: `Matching ${form.num_workers} ${form.trade} — see candidates below.` });
      await findMatches(form.trade);
    } catch {
      toast.error("Something went wrong. Try again.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <section id="employers" className="relative py-24 sm:py-32 border-b border-white/10 bg-[#10161F]">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="mb-12">
          <div className="font-mono-tech text-[11px] tracking-[0.25em] text-[#FF5E1E] mb-3">[ SEC—EMPLOYERS — BUILD YOUR WORKFORCE ]</div>
          <h2 className="font-display font-black uppercase tracking-tight text-3xl sm:text-4xl lg:text-5xl">Build your workforce</h2>
          <p className="mt-4 max-w-2xl text-[#8FA3B8]">Request trained, workforce-ready talent. Tell us what you need — we match verified candidates by trade, access readiness, and availability.</p>
        </div>

        <div className="grid lg:grid-cols-5 gap-6">
          {/* Dashboard preview */}
          <div className="lg:col-span-2 space-y-4">
            <div className="font-mono-tech text-[10px] tracking-[0.2em] text-[#8FA3B8]">WORKFORCE DASHBOARD — LIVE</div>
            <div className="grid grid-cols-2 gap-3">
              {EMPLOYER_DASH.map((d, i) => (
                <motion.div
                  key={d.label}
                  data-testid={`employer-stat-${i}`}
                  initial={{ opacity: 0, y: 16 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: i * 0.05 }}
                  className="bg-[#0B0F14] border border-white/10 p-4"
                >
                  <div className={`font-display font-black text-3xl ${TONE[d.tone]}`}>{d.value}</div>
                  <div className="font-mono-tech text-[9px] tracking-[0.12em] text-[#8FA3B8] mt-1 uppercase leading-tight">{d.label}</div>
                </motion.div>
              ))}
            </div>
          </div>

          {/* Request form */}
          <form onSubmit={submit} data-testid="employer-request-form" className="lg:col-span-3 bg-[#0B0F14] border border-white/10 p-6 sm:p-8">
            <div className="flex items-center gap-2 mb-6">
              <Building2 size={18} className="text-[#FF5E1E]" />
              <span className="font-display font-black uppercase text-xl">I need <span className="text-[#FF5E1E]">{form.num_workers || 0}</span> {form.trade || "workers"}</span>
            </div>

            <div className="grid sm:grid-cols-2 gap-4">
              <F label="Company"><Input data-testid="employer-company-input" value={form.company} onChange={(e) => set("company", e.target.value)} className="emp-input" placeholder="Acme Ship Repair" /></F>
              <F label="Contact name"><Input data-testid="employer-contact-input" value={form.contact_name} onChange={(e) => set("contact_name", e.target.value)} className="emp-input" placeholder="Jane Foreman" /></F>
              <F label="Work email"><Input data-testid="employer-email-input" type="email" value={form.contact_email} onChange={(e) => set("contact_email", e.target.value)} className="emp-input" placeholder="jane@acme.com" /></F>
              <F label="Trade needed"><Input data-testid="employer-trade-input" value={form.trade} onChange={(e) => set("trade", e.target.value)} className="emp-input" placeholder="Structural Welders" /></F>
              <F label="# of workers"><Input data-testid="employer-count-input" type="number" min="1" value={form.num_workers} onChange={(e) => set("num_workers", e.target.value)} className="emp-input" /></F>
              <F label="Location"><Input data-testid="employer-location-input" value={form.location} onChange={(e) => set("location", e.target.value)} className="emp-input" placeholder="Norfolk, VA" /></F>
              <F label="Start date"><Input data-testid="employer-startdate-input" value={form.start_date} onChange={(e) => set("start_date", e.target.value)} className="emp-input" placeholder="ASAP / 2026-08-01" /></F>
              <F label="Shift"><Input data-testid="employer-shift-input" value={form.shift} onChange={(e) => set("shift", e.target.value)} className="emp-input" placeholder="1st / 2nd / rotating" /></F>
            </div>
            <div className="mt-4">
              <F label="Requirements (certs, access, per diem, travel, duration)">
                <Textarea data-testid="employer-notes-input" value={form.notes} onChange={(e) => set("notes", e.target.value)} className="emp-input min-h-[80px]" placeholder="e.g. 2+ yrs, DBIDS access readiness, per diem, 6-month project" />
              </F>
            </div>

            <div className="mt-6 flex flex-col sm:flex-row gap-3">
              <Button type="submit" disabled={busy} data-testid="employer-submit-button" className="flex-1 rounded-none bg-[#FF5E1E] hover:bg-[#ff7038] text-[#0B0F14] font-display font-bold uppercase h-12 group">
                {busy ? "Submitting…" : <>Request Talent <Send className="ml-2 group-hover:translate-x-1 transition-transform" size={16} /></>}
              </Button>
              <Button type="button" onClick={() => findMatches(form.trade)} disabled={matching} data-testid="employer-preview-matches-button" variant="outline" className="rounded-none border-white/20 bg-transparent hover:bg-white/10 text-white font-display font-bold uppercase h-12">
                <Search className="mr-2" size={16} /> {matching ? "Matching…" : "Preview Matches"}
              </Button>
            </div>
          </form>
        </div>

        {matches && (
          <div className="mt-8" data-testid="employer-matches-panel">
            <div className="flex items-center justify-between mb-4">
              <div className="font-mono-tech text-[10px] tracking-[0.2em] text-[#FF5E1E]">MATCHING WORKFORCE-READY TALENT · {matches.length} FOUND</div>
              <span className="font-mono-tech text-[9px] tracking-[0.15em] text-[#8FA3B8]">VERIFIED CANDIDATES</span>
            </div>
            {matches.length === 0 ? (
              <p className="text-sm text-[#8FA3B8]">No workforce-ready candidates match that trade yet — try another trade or submit a request and we'll source them.</p>
            ) : (
              <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
                {matches.map((m, i) => (
                  <motion.div
                    key={m.user_id}
                    data-testid={`match-card-${i}`}
                    initial={{ opacity: 0, y: 16 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ delay: (i % 3) * 0.05 }}
                    className="bg-[#0B0F14] border border-white/10 hover:border-[#FF5E1E]/50 p-5 transition-colors"
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <span className="h-10 w-10 grid place-items-center bg-[#182230] text-[#FF5E1E] font-display font-black">{m.name.split(" ").map((n) => n[0]).join("").slice(0, 2)}</span>
                        <div>
                          <div className="font-display font-bold uppercase text-sm leading-tight">{m.name}</div>
                          <div className="font-mono-tech text-[9px] tracking-[0.12em] text-[#8FA3B8]">{m.trade} · {m.experience_level}</div>
                        </div>
                      </div>
                      {m.verified && <ShieldCheck size={16} className="text-[#10B981]" title="Verified" />}
                    </div>
                    <div className="mt-3 flex flex-wrap gap-1.5">
                      {m.skills.slice(0, 3).map((s) => (
                        <span key={s} className="font-mono-tech text-[9px] tracking-[0.1em] text-[#B8C4D0] border border-white/10 px-1.5 py-0.5">{s}</span>
                      ))}
                    </div>
                    <div className="mt-3 pt-3 border-t border-white/10 flex items-center justify-between text-xs text-[#8FA3B8]">
                      <span className="flex items-center gap-1"><MapPin size={12} className="text-[#FF5E1E]" /> {m.preferred_location}</span>
                      <span className="flex items-center gap-1"><Clock size={12} className="text-[#FF5E1E]" /> {m.availability}</span>
                    </div>
                  </motion.div>
                ))}
              </div>
            )}
          </div>
        )}
      </div>

      <style>{`
        .emp-input { background:#10161F; border:1px solid rgba(255,255,255,0.12); border-radius:0; color:#F4F6F8; }
        .emp-input:focus-visible { outline:none; border-color:#FF5E1E; box-shadow:0 0 0 1px #FF5E1E; }
      `}</style>
    </section>
  );
}

function F({ label, children }) {
  return (
    <div>
      <Label className="font-mono-tech text-[9px] tracking-[0.15em] text-[#8FA3B8] uppercase mb-1.5 block">{label}</Label>
      {children}
    </div>
  );
}
